import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cleanFloat, fmtNumber, fmtNumberFlex } from '@/domain/numbers';
import { formatRange } from '@/domain/time';
import { useData } from '@/state/data';
import { ProvenanceList } from './RecordViewer';
import { Note, SourceStatusBadge, CoverageBadge } from './Badges';
import { FIELD_LABELS, UNAVAILABLE_SHORT } from '@/lib/labels';
import { machineLabel } from '@/lib/present';

const METRIC_SOURCES = {
  tonnes: ['produksjon'],
  productionHours: ['produksjon'],
  stopHours: ['produksjon'],
  productionRate: ['produksjon'],
  engineHours: ['maskindata'],
  idleHours: ['maskindata'],
  fuelLitres: ['maskindata'],
  dieselPerTonne: ['maskindata', 'produksjon'],
  machineHoursPerTonne: ['maskindata', 'produksjon'],
  idleShare: ['maskindata'],
  costPerTonne: ['maskindata', 'produksjon'],
  co2PerTonne: ['maskindata', 'produksjon'],
  co2TotalKg: ['maskindata'],
};

/** Klikk på en KPI åpner teller, nevner, maskiner, periode og kildestatus. */
export function MetricExplainDialog({ metric, result, open, onClose }) {
  const { sources } = useData();
  if (!metric || !result) return null;
  const machines = result.machines.filter((m) => m.inScope);
  const srcIds = METRIC_SOURCES[metric.key] ?? [];
  const unavailable = metric.status === 'unavailable' || metric.value === null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="kpi-explain-dialog">
        <DialogHeader>
          <DialogTitle>Hvordan beregnes {metric.label.startsWith('CO₂') ? metric.label : metric.label.toLowerCase()}?</DialogTitle>
          <DialogDescription>
            {formatRange(result.query.fromDate, result.query.toDate)} · {result.scopeLabel}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 text-sm">
          {unavailable ? (
            <Note tone="amber" data-testid="kpi-explain-unavailable">
              <strong>{UNAVAILABLE_SHORT[metric.reason] ?? 'Utilgjengelig'}.</strong> {metric.message}
              {metric.missingFields?.length ? <div className="mt-1">Manglende felt: {metric.missingFields.map((f) => FIELD_LABELS[f] ?? f).join(', ')}</div> : null}
            </Note>
          ) : (
            <div className="hr-inset px-4 py-3">
              <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--hr-muted-2)]">Resultat</div>
              <div className="hr-num text-2xl font-semibold text-[var(--hr-heading)]" data-testid="kpi-explain-result">
                {fmtNumber(metric.value, metric.decimals)} <span className="text-base font-normal text-[var(--hr-muted)]">{metric.unit}</span>
              </div>
              <div className="hr-tabular mt-1 text-[13px] text-[var(--hr-muted)]">Intern verdi (inntil 6 desimaler, uten flyttallsstøy): {fmtNumberFlex(cleanFloat(metric.value, 6), 6)} {metric.unit}</div>
              {metric.provenance && <div className="mt-1 text-[13px] text-[var(--hr-muted)]">Opphav: {metric.provenance === 'reported' ? 'rapportert av kilde' : metric.provenance === 'estimated' ? 'estimert fra andre målinger' : 'blandet (rapportert + estimert, aldri for samme intervall)'}</div>}
            </div>
          )}
          {(metric.numerator || metric.denominator) && (
            <div className="grid gap-3 sm:grid-cols-2">
              {metric.numerator && (
                <div className="rounded-[var(--hr-radius-sm)] border border-[var(--hr-border)] px-4 py-3">
                  <div className="text-[13px] uppercase tracking-wide text-[var(--hr-muted)]">Teller</div>
                  <div className="mt-0.5 font-medium">{metric.numerator.label}</div>
                  <div className="hr-tabular text-lg" data-testid="kpi-explain-numerator">
                    {fmtNumberFlex(metric.numerator.value, 3)} {metric.numerator.unit}
                    {metric.numerator.partial && <span className="ml-2 text-[13px] text-[var(--hr-amber)]">registrert, delvis</span>}
                  </div>
                  <div className="text-[13px] text-[var(--hr-muted)]">{metric.numerator.recordIds.length} kildeposter</div>
                </div>
              )}
              {metric.denominator && (
                <div className="rounded-[var(--hr-radius-sm)] border border-[var(--hr-border)] px-4 py-3">
                  <div className="text-[13px] uppercase tracking-wide text-[var(--hr-muted)]">Nevner</div>
                  <div className="mt-0.5 font-medium">{metric.denominator.label}</div>
                  <div className="hr-tabular text-lg" data-testid="kpi-explain-denominator">
                    {fmtNumberFlex(metric.denominator.value, 3)} {metric.denominator.unit}
                  </div>
                  <div className="text-[13px] text-[var(--hr-muted)]">{metric.denominator.recordIds.length} kildeposter</div>
                </div>
              )}
            </div>
          )}
          {metric.note && <p className="text-[var(--hr-muted)]">{metric.note}</p>}
          {metric.key.startsWith('co2') && result.co2 && (
            <div className="rounded-[var(--hr-radius-sm)] border border-[var(--hr-border)] px-4 py-3" data-testid="kpi-explain-co2">
              <div className="text-[13px] uppercase tracking-wide text-[var(--hr-muted)]">CO₂-grunnlag</div>
              <dl className="mt-2 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <div><dt className="text-[13px] text-[var(--hr-muted)]">Opphav</dt><dd className="font-medium">{result.co2.sourceLabel}</dd></div>
                <div><dt className="text-[13px] text-[var(--hr-muted)]">Intervaller</dt><dd className="hr-tabular font-medium">{result.co2.reportedIntervals} rapportert · {result.co2.estimatedIntervals} estimert · {result.co2.missingIntervals} mangler</dd></div>
                <div><dt className="text-[13px] text-[var(--hr-muted)]">Faktor</dt><dd className="hr-tabular font-medium">{fmtNumber(result.co2.factor, 2)} {result.co2.factorUnit} <span className="font-normal text-[var(--hr-muted)]">(forutsetning)</span></dd></div>
                <div><dt className="text-[13px] text-[var(--hr-muted)]">Rapportert / estimert</dt><dd className="hr-tabular font-medium">{fmtNumber(result.co2.reportedKg, 0)} kg / {fmtNumber(result.co2.estimatedKg, 0)} kg</dd></div>
              </dl>
              <p className="mt-2 text-[13px] leading-5 text-[var(--hr-muted)]">{result.co2.method} {result.co2.priorityRule}</p>
            </div>
          )}
          <div>
            <div className="mb-1 text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">Periode</div>
            <div className="hr-tabular">
              {formatRange(result.query.fromDate, result.query.toDate)} (Europe/Oslo) · <span className="text-[13px] text-[var(--hr-muted)]">UTC [{result.fromUtc}, {result.toUtc})</span>
            </div>
            <div className="text-[13px] text-[var(--hr-muted)]">
              {result.productionDays} registrerte produksjonsdager · {result.plannedOffDays} planlagt driftsfrie dager
            </div>
          </div>
          {srcIds.includes('maskindata') && (
            <div>
              <div className="mb-1 text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">Maskiner i beregningsområdet ({machines.length})</div>
              <ul className="flex flex-wrap gap-1.5">
                {machines.map((m) => (
                  <li key={m.machineId} className="rounded-md border border-[var(--hr-border)] bg-white px-2 py-0.5 text-[13px]">
                    <span className="font-medium">{machineLabel(m.machineId)}</span> <span className="text-[var(--hr-muted)]">{m.name}</span>
                    {m.allocationMethod === 'demo_assumption' && <span className="ml-1 text-[var(--hr-amber)]">(anslått andel)</span>}
                    {m.missingFields.length > 0 && <span className="ml-1 text-[var(--hr-amber)]">(mangler {m.missingFields.map((f) => FIELD_LABELS[f] ?? f).join(', ').toLowerCase()})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="mb-1 text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">Kildestatus</div>
            <ul className="space-y-1.5">
              {sources
                .filter((s) => srcIds.includes(s.sourceId))
                .map((s) => (
                  <li key={s.sourceId} className="flex flex-wrap items-center gap-2 text-sm">
                    <SourceStatusBadge status={s.status} /> <span>{s.name}</span> <span className="text-[13px] text-[var(--hr-muted)]">{s.modeLabel}</span>
                  </li>
                ))}
            </ul>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {result.coverage.map((c) => (
                <CoverageBadge key={c.field} status={c.status} label={`${FIELD_LABELS[c.field] ?? c.field}: ${c.received}/${c.expected}`} />
              ))}
            </div>
          </div>
          {metric.numerator && <ProvenanceList recordIds={metric.numerator.recordIds} label="Kildeposter (teller)" testId="kpi-explain-provenance-numerator" />}
          {metric.denominator && <ProvenanceList recordIds={metric.denominator.recordIds} label="Kildeposter (nevner)" testId="kpi-explain-provenance-denominator" />}
          <p className="text-[13px] text-[var(--hr-muted)]">Uavrundede verdier internt; avrunding kun ved visning. Forholdstall = summert teller / summert nevner.</p>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="kpi-explain-close-button">
            Lukk
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
